import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Loader2, Star, PenLine } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const ic = "w-full rounded-lg bg-ink-900 border border-white/12 px-4 py-3 text-ice placeholder:text-mist focus:border-cyan/60 outline-none";

export function Stars({ value, onChange, size = 22, testId }) {
  return (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= value;
        const cls = on ? "fill-cyan text-cyan" : "text-white/25";
        return onChange ? (
          <button type="button" key={n} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`} onClick={() => onChange(n)}
            className="transition-transform hover:scale-110"><Star size={size} className={cls} /></button>
        ) : <Star key={n} size={size} className={cls} />;
      })}
    </div>
  );
}

export function ReviewForm({ page }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", rating: 0, text: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { setError("Please choose a star rating."); return; }
    setState("loading"); setError("");
    try { await api.post("/reviews", { ...f, page }); setState("done"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); setState("idle"); }
  };

  if (state === "done") {
    return (
      <div data-testid="review-success" className="max-w-xl mx-auto rounded-3xl glass p-8 text-center">
        <Check className="text-cyan mx-auto" size={28} />
        <h3 className="font-display text-xl text-white mt-3">Thank you for your review.</h3>
        <p className="text-ice/70 mt-2 text-sm">It'll appear on the site once it's been approved.</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto text-center">
      {!open && (
        <button data-testid="review-open" onClick={() => setOpen(true)} className="book-cta">
          <PenLine size={16} /> Leave a review
        </button>
      )}
      <AnimatePresence>
        {open && (
          <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} onSubmit={submit}
            data-testid="review-form" className="rounded-3xl glass p-6 sm:p-8 text-left space-y-4">
            <div>
              <p className="label mb-2">Your rating</p>
              <Stars value={f.rating} onChange={(rating) => setF({ ...f, rating })} size={28} testId="review-stars" />
            </div>
            <input data-testid="review-name" required maxLength={80} placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={ic} />
            <textarea data-testid="review-text" required minLength={5} maxLength={1200} rows={4} placeholder="How have the classes been for you?" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={ic} />
            {error && <p className="text-red-300 text-sm" data-testid="review-error">{error}</p>}
            <div className="flex items-center gap-4">
              <button data-testid="review-submit" disabled={state === "loading"} className="book-cta disabled:opacity-60">
                {state === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Submit review"}
              </button>
              <button type="button" data-testid="review-cancel" onClick={() => setOpen(false)} className="text-sm text-ice/60 hover:text-white transition-colors">Cancel</button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
